import ROBOTS_USER_AGENTS from "../constants/RobotList";

/**
 * Read the JavaScript cookies tutorial at:
 *   http://www.netspade.com/articles/javascript/cookies.xml
 */
export const isBrowser = typeof window !== "undefined";

/**
 * Sets a Cookie with the given name and value. Optionally, calculates the value based on a time offset.
 *
 * name       Name of the cookie
 * value      Value of the cookie or time offset in minutes
 * [days]     Number of days before expiration
 * [isTimeOffset] If true, treats value as a time offset in minutes
 */
export function createCookie(name, value, days, isTimeOffset = false) {
  if (isBrowser) {
    let expires = "";
    if (!Number.isNaN(days)) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toGMTString()}`;
    }

    const cookieValue = isTimeOffset
      ? new Date(new Date().getTime() + value * 60000).getTime()
      : value;

    document.cookie = `${name}=${cookieValue}${expires}; path=/`;
    return cookieValue;
  }
  return value;
}

export function isCrawlerBot() {
  if (isBrowser) {
    const { userAgent } = window.navigator;
    if (userAgent) {
      const lowerCaseUserAgent = userAgent.toLowerCase();
      return !!ROBOTS_USER_AGENTS.find((token) => lowerCaseUserAgent.includes(token.toLowerCase()));
    }
  }
  return false;
}

// Source: https://github.com/vyushin/ticks-to-date
export function ticksToDate(ticks) {
  return Number.isInteger(ticks)
    ? new Date(ticks / 1e4 + new Date("0001-01-01T00:00:00Z").getTime())
    : null;
}

/**
 * Sets a Cookie with the given name and value.
 *
 * name       Name of the cookie
 * value      Value of the cookie
 * [days]     Number of days before expiration
 * [ticks]    Expiration time in c# ticks
 */
export function createRootDomainCookie(name, value, days, ticks) {
  if (isBrowser) {
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toGMTString()}`;
    }
    if (ticks) {
      const dateTicks = ticksToDate(ticks);
      if (dateTicks) {
        expires = `; expires=${dateTicks.toGMTString()}`;
      }
    }
    window.location.hostname.split(".").slice(-2).join(".");
    document.cookie = `${name}=${value}${expires};${
      window.location.hostname === "localhost" ? "" : "domain=.analuisa.com;"
    }path=/`;
  }
  return value;
}

/**
 * Gets the value of the specified cookie.
 *
 * name  Name of the desired cookie.
 *
 * Returns a string containing value of specified cookie,
 *   or null if cookie does not exist.
 */
export function getCookie(name) {
  if (isBrowser) {
    const dc = document.cookie;
    const prefix = `${name}=`;
    let begin = dc.indexOf(`; ${prefix}`);
    if (begin === -1) {
      begin = dc.indexOf(prefix);
      if (begin !== 0) return null;
    } else {
      begin += 2;
    }
    let end = document.cookie.indexOf(";", begin);
    if (end === -1) {
      end = dc.length;
    }
    const c = unescape(dc.substring(begin + prefix.length, end));
    if (c === "[object Object]") return "";
    return c;
  }
  return null;
}

/**
 * Delete the specified cookie.
 *
 * @name {string} - Name of the cookie to delete.
 *
 */
export function deleteCookie(name) {
  createCookie(name, "", 0);
}

/**
 * Get all params from a URL
 *
 * Returns an object with values:
 *   - as string (if the key has one value)
 *   - as array (if a key has more values)
 *
 */
export function getAllUrlParams(url) {
  if (isBrowser) {
    const result = {};
    const urlCopy = url || window.location.href;
    const newUrl = new URL(urlCopy);
    const params = newUrl.searchParams;
    params.forEach((value, key) => {
      if (result[key]) {
        if (Array.isArray(result[key])) {
          result[key].push(value);
        } else {
          result[key] = [result[key], value];
        }
      } else {
        result[key] = value;
      }
    });

    return result;
  }
  return {};
}

export function addURLParam(urlParam, paramValue, url = null) {
  if (isBrowser) {
    const urlToChange = new URL(url || window.location.href);
    const params = new URLSearchParams(urlToChange.search);
    params.append(urlParam, paramValue);
    window.history.replaceState(
      null,
      "",
      `${urlToChange.origin}${urlToChange.pathname}?${params.toString()}`
    );
  }
}

export function removeURLParam(urlParam, paramValue = null, applyOnAll = false, url = null) {
  const urlToChange = new URL(url || window.location.href);
  let newParamsEntries = Array.from(new URLSearchParams(urlToChange.search).entries());
  if (applyOnAll) {
    newParamsEntries = newParamsEntries.filter(
      ([key, value]) => key !== urlParam || (paramValue !== null && paramValue !== value)
    );
  } else {
    const paramIndexToRemove = newParamsEntries.findIndex(
      ([key, value]) => key === urlParam && (paramValue === null || paramValue === value)
    );
    if (paramIndexToRemove > -1) {
      newParamsEntries.splice(paramIndexToRemove, 1);
    }
  }
  window.history.replaceState(
    null,
    "",
    `${urlToChange.origin}${urlToChange.pathname}${
      newParamsEntries.length ? `?${new URLSearchParams(newParamsEntries).toString()}` : ""
    }`
  );
}

/**
 * Update an URL query parameter
 * @param {string} urlParam - The key name of the parameter to update. If the key to update does not exist a new parameter is created.
 * @param {string} newValue - The new value of the parameter to update.
 * @param {string} previousValue - Previous value of the parameter. To specify if there are multiple parameters with the same key (array).
 * @param {boolean} applyOnAll - If `true` the update will apply on all matching parameters. Default `false`: the update applies only on the first found.
 * @param {string} url - URL to apply the update
 */
export function updateURLParam(
  urlParam,
  newValue,
  previousValue = null,
  applyOnAll = false,
  url = null
) {
  if (newValue === null) return removeURLParam(urlParam, previousValue, applyOnAll, url);
  const urlToChange = new URL(url || window.location.href);
  let paramsEntries = Array.from(new URLSearchParams(urlToChange.search).entries()); // [[key 1, value 1], [key 2, value 2]]
  const paramIndexToUpdate = paramsEntries.findIndex(
    ([key, value]) => key === urlParam && (previousValue === null || previousValue === value)
  );
  if (paramIndexToUpdate === -1) return addURLParam(urlParam, newValue, url); // Add the param if not found
  if (applyOnAll) {
    paramsEntries = paramsEntries.map(([key, value]) => {
      if (key === urlParam && (previousValue === null || previousValue === value)) {
        return [key, newValue];
      }
      return [key, value];
    });
  } else {
    paramsEntries[paramIndexToUpdate][1] = newValue;
  }
  window.history.replaceState(
    null,
    "",
    `${urlToChange.origin}${urlToChange.pathname}${
      paramsEntries.length ? `?${new URLSearchParams(paramsEntries).toString()}` : ""
    }`
  );
  return null;
}

export function gaCustomEvent(eventCategory, eventAction, eventLabel = null, eventValue = null) {
  // https://developers.google.com/analytics/devguides/collection/analyticsjs/events
  // ga('send', 'event', [eventCategory], [eventAction], [eventLabel], [eventValue], [fieldsObject]);
  // eventCategory	text	yes	Typically the object that was interacted with (e.g. 'Video')
  // eventAction	text	yes	The type of interaction (e.g. 'play')
  // eventLabel	text	no	Useful for categorizing events (e.g. 'Fall Campaign')
  // eventValue	integer	no	A numeric value associated with the event (e.g. 42)
  if (isBrowser && typeof window.ga !== "undefined") {
    if (!eventCategory && !eventAction) {
      console.warn("Missing parameters");
      return;
    }
    const data = {
      eventCategory,
      eventAction,
      eventLabel,
      eventValue,
    };

    window.ga("send", "event", data);
  }
}

export const removeQueryParam = (param) => {
  if (isBrowser) {
    const searchParams = new URLSearchParams(window.location.search);
    const entries = Object.fromEntries(searchParams.entries());
    if (!entries[param]) {
      return;
    }
    searchParams.delete(param);
    /* eslint-disable no-restricted-globals */
    if (history.replaceState) {
      const searchString = searchParams.toString().length > 0 ? `?${searchParams.toString()}` : "";
      const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${searchString}${window.location.hash}`;
      history.replaceState(null, "", newUrl);
    }
  }
};

export const blockedScriptsCache = new Set();

export const handleScriptError = (url, reject) => {
  fetch(url, { method: "HEAD" })
    .then((response) => {
      const responseDebugInfo = {
        url: response?.url,
        status: response?.status,
        statusText: response?.statusText,
        type: response?.type,
        redirected: response?.redirected,
        contentType: response?.headers?.get("content-type"),
      };
      console.log("Response:", responseDebugInfo);

      if (response?.ok) {
        const isBlocked = response?.url.startsWith("data:") || response?.url.startsWith("blob:");
        if (isBlocked) {
          console.warn("Script blocked by client (ad blocker):", url);
          blockedScriptsCache.add(url);
          reject(new Error(`Script blocked by client (ad blocker): ${url}`));
        } else {
          console.warn("Script loaded but execution failed:", url);
          reject(new Error(`Execution failure after loading: ${url}`));
        }
      } else {
        console.warn("Script loading failed with non-OK response:", url);
        reject(new Error(`Failed to load script with non-OK response: ${url}`));
      }
    })
    .catch((error) => {
      console.log("error: ", error);
      // If we enter this catch block, it means the fetch request itself failed
      console.warn("Script loading failed, likely blocked or network error:", url, error);
      blockedScriptsCache.add(url);
      reject(new Error(`Script loading failed, likely blocked or network error: ${url}`));
    });
};

export const loadScriptHead = (url) =>
  new Promise((resolve, reject) => {
    if (!document) {
      reject(new Error("Document was not defined"));
    }

    // Check if the script URL is already in the blocked cache
    if (blockedScriptsCache.has(url)) {
      reject(new Error(`Script is blocked or failed previously: ${url}`));
      return;
    }

    const oldScript = document.querySelector(`script[src='${url}']`);
    if (oldScript) {
      // Remove script
      oldScript.remove();
    }

    const head = document.getElementsByTagName("head")[0];
    const script = document.createElement("script");

    script.type = "text/javascript";
    script.src = url;
    script.async = true;

    script.onload = () => {
      resolve(script);
    };

    // Use the shared error handler
    script.onerror = () => handleScriptError(url, reject);

    script.onabort = (msg) => {
      console.log(msg);
      reject(new Error("Script loading aborted."));
    };

    head.appendChild(script);
  });

function generateUniqueID() {
  const time = Date.now().toString(36);
  const random = Number(`1${Math.random().toString().substring(2)}`.slice(1, 15)).toString(36);
  return random + time;
}

export function createSessionUserId() {
  const userId = generateUniqueID();
  if (isBrowser) {
    window.sessionStorage.setItem("sessionUserId", userId);
  }
  return userId;
}

export function getSessionUserId() {
  if (isBrowser) {
    const userId = window.sessionStorage.getItem("sessionUserId") || createSessionUserId();
    return userId;
  }
  return null;
}

/**
 * URL the user came from. On client-side navigations document.referrer is stale
 * (still the landing referrer), so we rely on the previous Gatsby location stored
 * in onPreRouteUpdate, and only fall back to document.referrer on the first page.
 */
export function setPreviousUrl(prevLocation) {
  if (isBrowser) {
    window.__previousUrl = prevLocation
      ? prevLocation.href ||
        `${window.location.origin}${prevLocation.pathname}${prevLocation.search || ""}`
      : null;
  }
}

export function getPreviousUrl() {
  if (isBrowser) {
    return window.__previousUrl || document.referrer || null;
  }
  return null;
}

const fallbackStorage = {};

function getItem(key) {
  try {
    if (!isBrowser) return null;
    if (typeof window.localStorage === "undefined" || !window.localStorage) {
      // Make sure to return null if the key is not found (undefined is not a valid value for localStorage)
      return fallbackStorage[key] || null;
    }
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function setItem(key, value) {
  try {
    if (!isBrowser) return;
    if (typeof window.localStorage === "undefined" || !window.localStorage) {
      fallbackStorage[key] = value;
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch (e) {
    fallbackStorage[key] = value;
  }
}

function removeItem(key) {
  if (!isBrowser) return;
  if (typeof window.localStorage !== "undefined" && !!window.localStorage) {
    window.localStorage.removeItem(key);
  }
  delete fallbackStorage[key];
}

export const safeLocalStorage = { getItem, setItem, removeItem };
